import { useEffect, useState } from 'react';
import { List, Pagination, Box, Typography, Container, TextField, InputAdornment } from '@mui/material';
import SimpleBackdrop from '../../components/Loading/SimpleBackdrop';
import { Chat } from './chat';
import ChatItem from './ChatItem';
import ChatModal from './ChatModal';
import { AlertInfo } from '../../components/Alerts/Alert';
import { api } from '../../services/api';
import moment from 'moment';
import { Search } from 'lucide-react';

const ITEMS_PER_PAGE = 100;

export default function ChatList() {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
    const [chats, setChats] = useState<Chat[]>([]);
    const [openLoad, setOpenLoad] = useState(false);
    const [search, setSearch] = useState("");
    const [totalPages] = useState(Math.ceil(chats.length / ITEMS_PER_PAGE));
    const [startIndex] = useState((currentPage - 1) * ITEMS_PER_PAGE);
    const [visibleChats, setVisibleChats] = useState<Chat[]>([]);

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handleChatClick = (chat: Chat) => {
        setSelectedChat(chat);
    };

    function filterResults() {
        if(search.length === 0) return setVisibleChats(chats.slice(startIndex, startIndex + ITEMS_PER_PAGE));
        const filter = chats.filter((c: Chat) => c.nome_cliente_message.includes(search.toUpperCase()));
        setVisibleChats(filter.slice(startIndex, startIndex + ITEMS_PER_PAGE));
    }

    async function loadData() {
        setOpenLoad(true);
        try {
            const response = await api.get(`/whatsappchats`);
            const arrayDeObjetos = response.data;

            const grupos = arrayDeObjetos.reduce((acumulador: any, itemAtual: any) => {
                const { id_cliente_message, nome_cliente_message, message_at, causa_processo_message, status_processo_message, telefone_message } = itemAtual;

                // Se o id_cliente_message ainda não existe no acumulador, inicialize-o
                if (!acumulador[id_cliente_message]) {
                    acumulador[id_cliente_message] = {
                        id_cliente_message,
                        nome_cliente_message,
                        messages: [],
                        message_at: null,
                        causa_processo_message: '',
                        status_processo_message: '',
                        whatsapp_cliente: '',
                    };
                }

                // Adicione o item atual ao array 'todos' do id_cliente_message correspondente
                acumulador[id_cliente_message].messages.push(itemAtual);

                const dataAtual = moment(message_at);

                if (acumulador[id_cliente_message].message_at === null || dataAtual.isAfter(moment(acumulador[id_cliente_message].message_at))) {
                    acumulador[id_cliente_message].message_at = dataAtual.format('DD/MM/YYYY HH:mm:ss');
                    acumulador[id_cliente_message].causa_processo_message = causa_processo_message;
                    acumulador[id_cliente_message].status_processo_message = status_processo_message;
                    acumulador[id_cliente_message].whatsapp_cliente = telefone_message;
                }

                return acumulador;
            }, {}); // O objeto inicial para o acumulador é vazio

            // Converta o objeto de grupos de volta para um array de objetos
            const result = Object.values<Chat>(grupos);
            setChats(result);
            setVisibleChats(result.slice(startIndex, startIndex + ITEMS_PER_PAGE))
            // setChats(result);
        } catch (error) {
            console.log(error);
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    useEffect(() => {
        loadData();
    }, []);

    return (
        <Box>
            <SimpleBackdrop open={openLoad} />
            <Container sx={{ p: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Buscar contato..."
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyUp={e => e.key === 'Enter' && filterResults()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search size={20} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Container>

            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {visibleChats.map((chat) => (
                    <ChatItem
                        key={chat.id_cliente_message}
                        chat={chat}
                        onClick={handleChatClick}
                    />
                ))}
                {chats.length === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', pt: 2 }}>
                        <Typography variant="h6" component="h1" color="text.primary" fontWeight="bold">
                            Nenhuma Mensagem Enviada
                        </Typography>
                    </Box>
                )}
            </List>

            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 2,
                bgcolor: 'grey.50',
                borderTop: 1,
                borderColor: 'grey.200'
            }}>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                />
            </Box>

            {selectedChat && (
                <ChatModal
                    chat={selectedChat}
                    onClose={() => setSelectedChat(null)}
                />
            )}
        </Box>
    );
}